import React from 'react';
import { useDashboardContext } from './DashboardContext.jsx';
import { permissions } from '@vanilla/services/PermissionService.js';
import { DashboardService } from '@vanilla/services/DashboardService.js';

export default function DashboardSwitcher({ className = '' }) {
  const dash = useDashboardContext();
  const [localAvailable, setLocalAvailable] = React.useState(null);
  const ctxAvailable = dash?.availableDashboards;
  const selectedKey = dash?.selectedKey;
  const selectDashboard = dash?.selectDashboard;

  const hasDashContext = Boolean(dash);

  React.useEffect(() => {
    if (!hasDashContext) return;
    let cancelled = false;
    async function load() {
      if (ctxAvailable?.length) {
        setLocalAvailable(ctxAvailable);
        return;
      }
      try {
        if (permissions.can('dashboard', 'manage')) {
          const all = await DashboardService.getAllDashboards().catch(() => []);
          if (!cancelled && all?.length) { setLocalAvailable(all); return; }
        }
        const my = await DashboardService.getMyDashboards().catch(() => []);
        if (!cancelled && my?.length > 1) setLocalAvailable(my);
      } catch {}
    }
    load();
    const onAuth = () => load();
    window.addEventListener('authChanged', onAuth);
    window.addEventListener('permissionsLoaded', onAuth);
    return () => { cancelled = true; window.removeEventListener('authChanged', onAuth); window.removeEventListener('permissionsLoaded', onAuth); };
  }, [hasDashContext, ctxAvailable]);

  const availableDashboards = ctxAvailable?.length ? ctxAvailable : localAvailable;
  if (!dash) return null;
  if (!availableDashboards?.length) return null;
  const selectedLabel = availableDashboards.find((d) => d.key === selectedKey)?.label || availableDashboards.find((d) => d.key === selectedKey)?.title || '';
  const dynamicWidth = Math.min(260, Math.max(120, selectedLabel.length * 7.5 + 32));
  return (
    <select
      value={selectedKey || ''}
      onChange={(e) => selectDashboard(e.target.value)}
      aria-label="Select dashboard"
      style={{ width: `${dynamicWidth}px` }}
      className={`shrink-0 h-8 min-h-[32px] border border-[var(--border-default)] rounded-md bg-[var(--surface-elevated)] text-[var(--text-primary)] text-xs font-medium px-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/20 ${className}`}
    >
      {availableDashboards.map((d) => (
        <option key={d.key} value={d.key}>
          {d.label || d.title}
        </option>
      ))}
    </select>
  );
}
